import { useEffect } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import Footer from "./components/sections/Footer/Footer";
import Navbar from "./components/sections/Navbar/Navbar";
import ProfileCover from "./components/sections/ProfileCover/ProfileCover";
import Sidebar from "./components/sections/Sidebar/Sidebar";
import NavRoutes from "./components/sections/Navbar/NavRoutes";

// Custom Cursor Component
function CustomCursor() {
  useEffect(() => {
    if (window.matchMedia("(hover: none)").matches) return; // Skip on touch devices

    const dot = document.createElement("div");
    dot.className = "cursor-dot";
    document.body.appendChild(dot);

    let mouseX = 0, mouseY = 0;

    const moveCursor = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      dot.style.left = mouseX + "px";
      dot.style.top = mouseY + "px";
    };

    const onHover = () => dot.classList.add("active");
    const onLeave = () => dot.classList.remove("active");

    window.addEventListener("mousemove", moveCursor);
    document.querySelectorAll("a, button, .card, input").forEach(el => {
      el.addEventListener("mouseenter", onHover);
      el.addEventListener("mouseleave", onLeave);
    });

    return () => {
      window.removeEventListener("mousemove", moveCursor);
      dot.remove();
    };
  }, []);

  return null;
}

// Scroll reveal intersection observer
function useScrollReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-slide-in-up");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll("[data-scroll-reveal]").forEach(el => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);
}

function App() {
  useScrollReveal();

  return (
    <Router>
      <CustomCursor />
      <Navbar />
      <main className="min-h-screen relative bg-white transition-colors duration-300">
        {/* Profile Cover with floating sidebar */}
        <div className="relative">
          <ProfileCover />
          
          {/* Sidebar floating on cover at text level - Desktop only */}
          <div className="hidden lg:block">
            <div className="absolute right-0 left-0 top-80 md:top-96 lg:top-[360px] z-20 pointer-events-none">
              <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 pointer-events-auto">
                <div className="w-80">
                  <Sidebar />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main content area - starts below cover */}
        <div className="relative pt-2 md:pt-2 lg:pt-2 pb-2">
          <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
              {/* Desktop sidebar spacing - fixed to actual sidebar width */}
              <div className="hidden lg:block lg:w-80" />
              
              {/* Main content */}
              <div className="w-full lg:flex-1 relative z-20">
                <NavRoutes />
              </div>
            </div>
          </div>
        </div>

        {/* Mobile sidebar - visible only on mobile */}
        <div className="lg:hidden px-4 sm:px-6 md:px-0">
          <div className="max-w-8xl mx-auto md:w-80 md:mx-auto">
            <Sidebar />
          </div>
        </div>

        <Footer />
      </main>
    </Router>
  );
}

export default App;
